import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { FaFilePdf, FaDownload, FaArrowRight } from 'react-icons/fa';
import AppUrl from '../../restApi/AppUrl';
import './AnnualReports.css';
import SectionHeader from '../SectionHeader/SectionHeader';

const extractYear = (title) => {
  const match = title && title.match(/\b(19|20)\d{2}\b/);
  return match ? match[0] : null;
};

class AnnualReports extends Component {
  constructor(props) {
    super(props);
    this.state = {
      annualReports: [],
      loading: true,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchReports();
  }

  fetchReports = async () => {
    try {
      const response = await fetch(AppUrl.AnnualReports);
      if (!response.ok) throw new Error('Failed to fetch');
      const reports = await response.json();
      this.setState({ annualReports: reports, loading: false });
    } catch {
      this.setState({ error: true, loading: false });
    }
  };

  handleDownload = (report) => {
    if (!report.pdf_file) {
      alert('No PDF file available for this report.');
      return;
    }
    const link = document.createElement('a');
    link.href = AppUrl.DownloadAnnualReport + report.id;
    link.download = `${report.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { annualReports, loading, error } = this.state;

    if (loading || error || annualReports.length === 0) return null;

    // Show first 3 on homepage; "View All" button appears only when total is 4+
    const displayReports = annualReports.slice(0, 3);
    const hasMore = annualReports.length >= 4;

    return (
      <section style={{
        background: '#f8fafc',
        padding: '80px 0',
        borderTop: '1px solid #e2e8f0',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>

          {/* ── Section Header ── */}
          <SectionHeader
            badge="Transparency & Accountability"
            title="Annual Reports"
            accentWord="Reports"
            subtitle="A transparent record of our work — download our annual reports to follow SOYAN's journey of community impact."
            variant="light"
            align="center"
            className="mb-14"
          />

          {/* ── Reports List ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '48px' }}>
            {displayReports.map((report, index) => {
              const year = extractYear(report.title) ||
                (report.created_at ? new Date(report.created_at).getFullYear() : '');

              return (
                <ReportRow
                  key={report.id}
                  report={report}
                  year={year}
                  index={index}
                  onDownload={this.handleDownload}
                />
              );
            })}
          </div>

          {/* ── View All Button (4+ reports only) ── */}
          {hasMore && (
            <div style={{ textAlign: 'center' }}>
              <Link
                to="/annual-reports"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 36px',
                  background: '#2E8B54',
                  color: '#fff',
                  borderRadius: '100px',
                  fontWeight: '700',
                  fontSize: '15px',
                  textDecoration: 'none',
                  boxShadow: '0 4px 20px rgba(46,139,84,0.30)',
                  transition: 'all 0.25s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#237a45';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 28px rgba(46,139,84,0.40)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = '#2E8B54';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(46,139,84,0.30)';
                }}
              >
                View All Reports
                <span style={{
                  background: 'rgba(255,255,255,0.25)',
                  borderRadius: '100px',
                  padding: '2px 10px',
                  fontSize: '13px',
                  fontWeight: '800',
                }}>
                  {annualReports.length}
                </span>
                <FaArrowRight style={{ fontSize: '13px' }} />
              </Link>
            </div>
          )}

        </div>
      </section>
    );
  }
}

/* ── Individual Report Row ── */
class ReportRow extends Component {
  constructor(props) {
    super(props);
    this.state = { hovered: false };
  }

  render() {
    const { report, year, index, onDownload } = this.props;
    const { hovered } = this.state;

    const accentColors = ['#2E8B54', '#1B4F8A', '#7C3AED'];
    const accent = accentColors[index % accentColors.length];

    return (
      <div
        onMouseEnter={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          background: '#fff',
          borderRadius: '14px',
          padding: '20px 24px',
          border: '1px solid',
          borderColor: hovered ? accent : '#e2e8f0',
          borderLeft: `4px solid ${accent}`,
          boxShadow: hovered
            ? '0 8px 30px rgba(0,0,0,0.10)'
            : '0 2px 8px rgba(0,0,0,0.05)',
          transition: 'all 0.25s ease',
          cursor: 'default',
          flexWrap: 'wrap',
        }}
      >
        {/* PDF Icon */}
        <div style={{
          flexShrink: 0,
          width: '52px',
          height: '52px',
          background: '#fff1f2',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <FaFilePdf style={{ fontSize: '24px', color: '#e11d48' }} />
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
            <h3 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: '700',
              color: '#0f172a',
              lineHeight: '1.3',
            }}>
              {report.title}
            </h3>
            {year && (
              <span style={{
                background: '#f0fdf4',
                color: '#15803d',
                fontSize: '11px',
                fontWeight: '700',
                padding: '2px 10px',
                borderRadius: '100px',
                border: '1px solid #bbf7d0',
                whiteSpace: 'nowrap',
              }}>
                {year}
              </span>
            )}
          </div>
          {report.description && (
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#64748b',
              lineHeight: '1.6',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {report.description}
            </p>
          )}
        </div>

        {/* Download Button */}
        <button
          onClick={() => onDownload(report)}
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 22px',
            background: hovered ? accent : '#f1f5f9',
            color: hovered ? '#fff' : '#334155',
            border: 'none',
            borderRadius: '100px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.25s ease',
            whiteSpace: 'nowrap',
          }}
        >
          <FaDownload style={{ fontSize: '13px' }} />
          Download PDF
        </button>
      </div>
    );
  }
}

export default AnnualReports;
